import { onBeforeMount, reactive, ref, watch, getCurrentInstance } from "@vue/composition-api";
import router from "@/router";
import store from "@/store";
import flashdealEntryModel from "@/models/flashdeal/flashdeal-entry";
import useFilterHeaderTable from "@/views/components/filter-header-table/useFilterHeaderTable";
import usePagination from "@/views/components/pagination/usePagination";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import useDate from "@/utils/hooks/useDate";
import useModal from "@/@core/components/base-modal/hooks/useModal";

export default function useCampaignEntryList() {
  const vm = getCurrentInstance().proxy;
  const {
    fetchFlashDealEntryList,
    fetchFlashDealEntryReorder,
    fetchFlashDealEntryChangeStatus,
    fetchFlashDealEntryDelete,
    fetchFlashDealEntryDetail,
    fetchFlashDealEntryEdit
  } = flashdealEntryModel();
  const {
    openDefaultErrorModal,
    openWarningModal,
    openSuccessModal,
  } = useValidationModal();

  const { pagination, onPageChange, onFilterEntity } = usePagination();
  const { resolveDateWithSeperator, resolveTime } = useDate();
  const { openModal, closeModal } = useModal();

  const {
    filters,
    ordering,
    onSetValueSearch,
    onSelectFilter,
  } = useFilterHeaderTable();

  const loading = reactive({
    tableData: false,
  });
  const fields = [
    { key: "seq", label: "No" },
    { key: "pv_image_url", label: "Image" },
    { key: "pv_name_th", label: "Privilege Name" },
    { key: "im_name", label: "Inventory Name" },
    { key: "pv_point_ref", label: "Point" },
    { key: "criteria_point", label: "New Point" },
    { key: "pv_valid_from", label: "Valid From" },
    { key: "pv_valid_through", label: "Valid Through" },
    { key: "pv_status", label: "Status" },
    { key: "status", label: "Open/Close" },
    { key: "actions", label: "" },
  ];

  const statusOption = ref([
    { label: "Open", value: 1 },
    { label: "Close", value: 2 },
  ]);

  const status = ref(0);
  const tableModeReorder = ref(false);
  const keyChange = ref(0);
  const currentIndex = ref(null);
  const currentIdSelect = ref(null);
  const privilegeReorderSelect = ref({});
  const privilegeEditSelect = reactive({
    campaign : "",
    inventory : "",
    privilegeName : "",
    privilegeImage : "",
    reference : "",
    status : "",
    privilegeType : "",
    point : "",
    newPoint : "",
    validFrom : "",
    validThrough : ""
  });

  const privilegeEntryList = reactive({
    payload: [],
  });

  const modalType = reactive({
    reorder: false,
    editModal : false,
  });

  const resolveMappingTableAction = (type, item) => {
    switch (type) {
      case 1:
        modalType.editModal = true;
        vm.$bvModal.show("flashdeal-edit-privilege");
        fetchEntryDetail(item.item);
        openModal();
        return;
      case 2:
        modalType.reorder = true;
        openReorderModal(item.item);
        return;
      case 3:
        removeEntry(item.item);
        return;
      default:
    }
  };

  const privilegeActionList = reactive({
    edit: {
      label: "Edit",
      value: 1,
      icon: "EditIcon",
      display: true,
    },
    reOrder: {
      label: "Reorder",
      value: 2,
      icon: "ReorderIcon",
      display: false,
    },
    delete: {
      label: "Delete",
      value: 3,
      icon: "DeleteIcon",
      display: true,
    },
  });

  const fetchEntryList = (showLoading = true) => {
    if (showLoading) {
      loading.tableData = true;
    }
    fetchFlashDealEntryList.payload.Campaign_Alt_Ref = router.currentRoute.params.ref;
    fetchFlashDealEntryList.payload.Page = pagination.currentPage;
    fetchFlashDealEntryList.payload.Limit = pagination.perPage;
    fetchFlashDealEntryList.payload.Filters = filters;
    store
      .dispatch("api/apiRequest", fetchFlashDealEntryList)
      .then((response) => {
        privilegeEntryList.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const fetchEntryDetail = (entry) => {
    window.$loading(true);
    fetchFlashDealEntryDetail.payload.Privilege_Alt_Ref = entry.pv_alt_reference;
    fetchFlashDealEntryDetail.payload.Inventory_Alt_Ref = entry.im_inv_ref;
    fetchFlashDealEntryDetail.payload.Campaign_Alt_Ref = entry.campaign_alt_ref;
    store.dispatch('api/apiRequest', fetchFlashDealEntryDetail).then(response => {
        let resp = response.data.resp
        privilegeEditSelect.campaign = entry.campaign_alt_ref;
        privilegeEditSelect.inventory = entry.im_inv_ref;
        privilegeEditSelect.privilegeName = resp.pv_name_th
        privilegeEditSelect.reference = resp.pv_alt_reference
        privilegeEditSelect.privilegeImage = resp.pv_image_url
        privilegeEditSelect.point = resp.pv_point_ref
        privilegeEditSelect.newPoint = resp.criteria_point
        privilegeEditSelect.privilegeType = resp.pv_type
        privilegeEditSelect.validFrom = resolveDateWithSeperator(resp.pv_valid_from)
        privilegeEditSelect.validThrough = resolveDateWithSeperator(resp.pv_valid_through)
        privilegeEditSelect.status = resolveStatusLabel(resp.pv_status)
    })
    .catch(error => {
        openDefaultErrorModal(error);
    })  
    .finally(() => {
        window.$loading(false);
    })
  }

  const fetchEntryEdit = (obj) => {
    window.$loading(true);
    fetchFlashDealEntryEdit.payload.Campaign_Alt_Ref = privilegeEditSelect.campaign
    fetchFlashDealEntryEdit.payload.Inventory_Alt_Ref = privilegeEditSelect.inventory
    fetchFlashDealEntryEdit.payload.Privilege_Alt_Ref = privilegeEditSelect.reference
    fetchFlashDealEntryEdit.payload.Point = privilegeEditSelect.newPoint
    store.dispatch('api/apiRequest', fetchFlashDealEntryEdit).then(response => {
        openSuccessModal(null,null,null,null,() => {
            closeEditModal();
            fetchEntryList();
        });
    })
    .catch(error => {
        openDefaultErrorModal(error);
    })  
    .finally(() => {
        window.$loading(false);
    })
  }

  const changeEntryStatus = (entry, status) => {
    loading.tableData = true;
    fetchFlashDealEntryChangeStatus.payload.Privilege_Alt_Ref =
      entry.pv_alt_reference;
    fetchFlashDealEntryChangeStatus.payload.Campaign_Alt_Ref =
      entry.campaign_alt_ref;
    fetchFlashDealEntryChangeStatus.payload.Inventory_Alt_Ref =
      entry.im_inv_ref;
    fetchFlashDealEntryChangeStatus.payload.Status = status ? 1 : 2;
    store
      .dispatch("api/apiRequest", fetchFlashDealEntryChangeStatus)
      .then((response) => {})
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        fetchEntryList();
      });
  };

  const removeEntry = (entry) => {
    openWarningModal(
      "ลบ Privilege",
      `ต้องการลบ ${entry.pv_name_th} นี้ใช่หรือไม่`,
      () => {
        loading.tableData = true;
        fetchFlashDealEntryDelete.payload.Privilege_Alt_Ref =
          entry.pv_alt_reference;
        fetchFlashDealEntryDelete.payload.Campaign_Alt_Ref =
          entry.campaign_alt_ref;
        fetchFlashDealEntryDelete.payload.Inventory_Alt_Ref = entry.im_inv_ref;
        store
          .dispatch("api/apiRequest", fetchFlashDealEntryDelete)
          .then((response) => {
            openSuccessModal();
          })
          .catch((error) => {
            openDefaultErrorModal(error);
          })
          .finally(() => {
            fetchEntryList();
          });
      }
    );
  };

  const resolveStatusLabel = (status) => {
    switch (status) {
      case 1:
        return "Active";
      case 2:
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  const resolveStatusClass = (status) => {
    switch (status) {
      case 1:
        return "active-icon";
      case 2:
        return "inActive-icon";
      default:
        return "Unknown";
    }
  };

  const onClickReorder = () => {
    privilegeActionList.reOrder.display = !privilegeActionList.reOrder.display;
    tableModeReorder.value = !tableModeReorder.value;
    sortableRowOptions.value.disabled = !sortableRowOptions.value.disabled;
  };

  const handleRowChange = (event) => {
    privilegeReorderSelect.value =
      privilegeEntryList.payload[event.oldDraggableIndex];
    fetchReorderList(
      privilegeEntryList.payload[event.oldDraggableIndex].pv_alt_reference,
      privilegeEntryList.payload[event.oldDraggableIndex].seq,
      privilegeEntryList.payload[event.newDraggableIndex].seq
    );
    privilegeEntryList.payload.splice(
      event.newDraggableIndex,
      0,
      privilegeEntryList.payload.splice(event.oldDraggableIndex, 1)[0]
    );
    keyChange.value++;
  };

  const fetchReorderList = (id, currentPosition, newPosition) => {
    loading.tableData = true;
    fetchFlashDealEntryReorder.payload.Privilege_Alt_Ref = id;
    fetchFlashDealEntryReorder.payload.Campaign_Alt_Ref =
      privilegeReorderSelect.value.campaign_alt_ref;
    fetchFlashDealEntryReorder.payload.Inventory_Alt_Ref =
      privilegeReorderSelect.value.im_inv_ref;
    fetchFlashDealEntryReorder.payload.From = currentPosition;
    fetchFlashDealEntryReorder.payload.To = newPosition;
    store
      .dispatch("api/apiRequest", fetchFlashDealEntryReorder)
      .then((response) => {
        fetchEntryList(false);
      })
      .catch((error) => {
        openDefaultErrorModal(error);
        loading.tableData = false;
      });
  };

  const openReorderModal = (item) => {
    currentIndex.value = item.seq;
    currentIdSelect.value = item.pv_alt_reference;
    privilegeReorderSelect.value = item;
    openModal();
  };

  const reorderPrivilege = async (current, newIndex) => {
    fetchReorderList(currentIdSelect.value, currentIndex.value, newIndex);

    keyChange.value++;
    modalType.reorder = false;
    closeModal();
  };

  const sortableRowOptions = ref({
    chosenClass: "is-selected",
    ghostClass: "sortable-ghost",
    onEnd: handleRowChange,
    disabled: true,
    animation: 300,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
  });

  const closeEditModal = () => {
    privilegeEditSelect.privilegeName = ""
    privilegeEditSelect.reference = ""
    privilegeEditSelect.privilegeImage = ""
    privilegeEditSelect.point = ""
    privilegeEditSelect.newPoint = ""
    privilegeEditSelect.privilegeType = ""
    privilegeEditSelect.validFrom = ""
    privilegeEditSelect.validThrough = ""
    privilegeEditSelect.status = ""
    modalType.editModal = false;
    closeModal();
  }

  onBeforeMount(() => {
    if (router.currentRoute.params.id) {
      fetchEntryList();
    }
  });

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchEntryList();
      }
    },
    { deep: true }
  );

  watch(
    [filters, ordering],
    () => {
      pagination.currentPage = 1;

      fetchEntryList();
    },
    { deep: true }
  );

  return {
    modalType,
    fields,
    loading,
    status,
    statusOption,
    privilegeEntryList,
    filters,
    ordering,
    pagination,
    privilegeActionList,
    sortableRowOptions,
    tableModeReorder,
    keyChange,
    currentIndex,
    privilegeEditSelect,
    fetchEntryList,
    reorderPrivilege,
    onSetValueSearch,
    onSelectFilter,
    onPageChange,
    onFilterEntity,
    resolveDateWithSeperator,
    resolveTime,
    changeEntryStatus,
    resolveStatusLabel,
    resolveStatusClass,
    resolveMappingTableAction,
    onClickReorder,
    closeEditModal,
    fetchEntryEdit
  };
}
